import { useMemo } from 'react'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'
import type { UseFiltersPayload } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditInvoiceModal from '@modals/CreateOrEditInvoiceModal'

import type { InvoiceModel } from '@models/types'

import PageContent from './components/PageContent'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    showCreateOrEditInvoiceModal: () => void,
  },
}

const defaultState = {
  showCreateOrEditInvoiceModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditInvoiceModal: {
      closeModal: () => setState({ showCreateOrEditInvoiceModal: false }),
      createInvoice: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateInvoice: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const { showCreateOrEditInvoiceModal } = state

  const filtersPayload = useFilters({ defaultFilters: { approved: 'true' } })
  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  } = filtersPayload

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateOrEditInvoiceModal: (payload: { invoice: InvoiceModel }) => {
        setModalData('CreateOrEditInvoiceModal', payload)
        setState({ showCreateOrEditInvoiceModal: true })
      },
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt]) // modalContext dependency required if layering modals

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <CreateOrEditInvoiceModal
          callbacks={callbacks('CreateOrEditInvoiceModal', setState)}
          showModal={showCreateOrEditInvoiceModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
